import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { IEvent } from '../../store/events/interfaces';

import {
  Badge,
  Col,
  Overlay,
  OverlayTrigger,
  ProgressBar,
  Row,
  Tooltip,
} from 'react-bootstrap';

export const EventOccupation = (props: { event: IEvent; isModal?: boolean }) => {
  const { event, isModal } = props;
  const { data: program } = useSelector((state: RootState) => state.program);
  const targets = useRef<Array<HTMLDivElement | null>>([]);
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [show3, setShow3] = useState(false);
  const shows = [show1, show2, show3];
  const setShows = [setShow1, setShow2, setShow3];
  const occupationResults: [string, number, boolean, boolean, string][] = [];

  const genderInfo = event.registration_statistics.gender_info_combined;
  const noParticipantsAllowed = 'none' in genderInfo;

  const genderSymbols: Record<string, string> = {
    w: '♀',
    m: '♂️',
    d: '⚲',
    mw: '♂+♀',
    md: '♂+⚲',
    wd: '♀+⚲',
    mwd: '♂+♀+⚲',
  };

  const showAllOccupation = program.count_all_occupations && program.distribution_type !== 'INSTANT';


  for (const [key, value] of Object.entries(genderInfo)) {
    let currentOccupation = value[0];
    if (key === "mwd") {
      currentOccupation = event.registration_statistics.effectively_occupied_places_amount
    }
    const maxOccupation = value[1];
    const isUnlimited = maxOccupation === null || maxOccupation === undefined;
    const isFullOccupation = currentOccupation >= maxOccupation;
    let occupationPercentage;

    if (isUnlimited) {
      occupationPercentage = 0;
    } else if (isFullOccupation) {
      occupationPercentage = 100;
    } else {
      const totalSlots = maxOccupation + event.registration_statistics.waiting_list_occupied;
      const effectiveOccupation = showAllOccupation
        ? currentOccupation + event.registration_statistics.amount_unread
        : currentOccupation;
      occupationPercentage = (effectiveOccupation / totalSlots) * 100;
    }

    if (noParticipantsAllowed) {
      occupationResults.push([
        'Keine Teilnahme erlaubt',
        100,
        true,
        false,
        'none',
      ]);
    } else {
      const effectiveOccupation = showAllOccupation
        ? currentOccupation + event.registration_statistics.amount_unread
        : currentOccupation;
      occupationResults.push([
        program.booking_visibility === 'detailed'
          ? `${genderSymbols[key]} - ${
              isUnlimited
                ? 'Unbegrenzt'
                : `${effectiveOccupation}/${maxOccupation !== null ? maxOccupation : '∞'}`
            }`
          : genderSymbols[key],
        occupationPercentage,
        isFullOccupation || (showAllOccupation && effectiveOccupation >= maxOccupation),
        isUnlimited,
        key,
      ]);
    }
  }

  function genderText(result: [string, number, boolean, boolean, string]) {
    const [label, percent, isFullOccupation, isUnlimited, genderKey] = result;

    if (isUnlimited) {
      return ' Für diese Veranstaltung gibt es keine Teilnehmerbegrenzung.'; // Message for unlimited participants
    } else if (isFullOccupation && event.registration_statistics.is_waiting_list_full) {
      return ' Die Veranstaltung ist komplett belegt.'; // Display a message indicating no registrations are possible
    }

    let occupancyText: string;

    if (percent < 100) {
      occupancyText = ' Es sind noch Plätze frei für ';
    }
    else if (!event.registration_statistics.is_waiting_list_full) {
      occupancyText = ' Es sind noch Wartelistenplätze frei für '
    }
    else {
      occupancyText = ' Es sind keine Plätze mehr frei für ';
    }

    switch (genderKey) {
      case 'w':
        return occupancyText + 'weibliche Teilnehmer.';
      case 'm':
        return occupancyText + 'männliche Teilnehmer.';
      case 'd':
        return occupancyText + 'diverse Teilnehmer.';
      case 'mw':
        return occupancyText + 'männliche und weibliche Teilnehmer.';
      case 'md':
        return occupancyText + 'diverse und männliche Teilnehmer.';
      case 'wd':
        return occupancyText + 'diverse und weibliche Teilnehmer.';
      case 'mwd':
        return occupancyText + 'diverse, männliche und weibliche Teilnehmer.';
    }
  }

  let totalCodeSlots = 0;

  if (event.get_remaining_soft_quota_slots) {
    const codeSlots = (event.get_remaining_soft_quota_slots as unknown) as {
      [key: string]: number;
    };

    totalCodeSlots = Object.values(codeSlots).reduce(
      (a: number, b: number) => a + b,
      0
    );
  }

  return (
    <>
      {occupationResults.map((result, key) => {
        const [label, percent, isFullOccupation, isUnlimited] = result;

        let progressBarVariant;

        if (isUnlimited) {
            progressBarVariant = '';
        } else if (isFullOccupation) {
            progressBarVariant = event.registration_statistics.is_waiting_list_full ? 'danger' : 'warning';
        } else if (!event.registration_statistics.is_waiting_list_full && isFullOccupation) {
            progressBarVariant = 'warning';
        } else {
            progressBarVariant = 'success';
        }


        const progressBarNow = isUnlimited ? 0 : isFullOccupation ? 100 : percent;

        return (
          <Row key={key}>
            <Col
              md={12}
              className='d-flex justify-content-center align-items-start'
            >
              <Badge>{label}</Badge>
              <div className='sr-only' tabIndex={0}>
                Aktuelle Belegung für die Veranstaltung "{event.name}".
                {genderText(result)}
              </div>
            </Col>
            <Col md={{ span: 10, offset: 1 }}>
              <div
                ref={(el) => (targets.current[key] = el)}
                onClick={() => setShows[key](!shows[key])}
                tabIndex={-1}
                onBlur={() =>
                  shows[key] ? setShows[key](!shows[key]) : null
                }
              >
                <ProgressBar
                  now={progressBarNow}
                  variant={progressBarVariant}
                  className='mb-2'
                  aria-label='Belegung nach Geschlecht'
                />
                {program.booking_visibility_codes !== 'never' &&
                  !isModal &&
                  totalCodeSlots > 0 && (
                    <OverlayTrigger
                      overlay={
                        <Tooltip id='tooltip-disabled'>
                          Weitere Informationen zu den Code Belegungen finden Sie
                          in der Detailansicht dieser Veranstaltung
                        </Tooltip>
                      }
                    >
                      <span className='small text-muted'>
                        {totalCodeSlots} verfügbare Codekategorie Plätze
                      </span>
                    </OverlayTrigger>
                  )}
              </div>
              {targets.current[key] && (
                <Overlay
                  target={targets.current[key]}
                  show={shows[key]}
                  placement='bottom'
                >
                  {(props: any) => (
                    <Tooltip {...props}>
                      Aktuelle Belegung für die Veranstaltung "{event.name}".
                      {genderText(result)}
                      {showAllOccupation && (
                        <div>
                          Hinweis: Die Belegung enthält auch Anmeldungen die noch nicht zugeteilt wurden.
                        </div>
                      )}
                    </Tooltip>
                  )}
                </Overlay>
              )}
            </Col>
          </Row>
        );
      })}
    </>
  );
};
